const BASE = '/api';

// ─── Token storage ─────────────────────────────────────────────────────────
export const tokenStore = {
  get: () => localStorage.getItem('sb_token'),
  set: (t) => localStorage.setItem('sb_token', t),
  clear: () => localStorage.removeItem('sb_token'),
};

function toQS(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
  const s = qs.toString();
  return s ? '?' + s : '';
}

async function req(url, options = {}) {
  const token = tokenStore.get();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(BASE + url, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  if (res.status === 401) { tokenStore.clear(); window.location.reload(); throw new Error('Session expired'); }
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try { msg = JSON.parse(text).error || text; } catch {}
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  // Auth
  login: (data) => req('/auth/login', { method: 'POST', body: data }),
  me: () => req('/auth/me'),
  changePassword: (data) => req('/auth/change-password', { method: 'POST', body: data }),

  // Users (admin)
  getUsers: () => req('/users'),
  createUser: (data) => req('/users', { method: 'POST', body: data }),
  updateUser: (id, data) => req(`/users/${id}`, { method: 'PUT', body: data }),
  deleteUser: (id) => req(`/users/${id}`, { method: 'DELETE' }),

  // Categories (small list — no pagination needed)
  getCategories: () => req('/categories'),
  createCategory: (data) => req('/categories', { method: 'POST', body: data }),
  updateCategory: (id, data) => req(`/categories/${id}`, { method: 'PUT', body: data }),
  setCategoryActive: (id, isActive) => req(`/categories/${id}/active`, { method: 'PATCH', body: { isActive } }),

  // Products — backend paginated
  getProducts: (params = {}) => req('/products' + toQS(params)),
  getProductsLookup: () => req('/products/lookup'),
  createProduct: (data) => req('/products', { method: 'POST', body: data }),
  updateProduct: (id, data) => req(`/products/${id}`, { method: 'PUT', body: data }),
  setProductActive: (id, isActive) => req(`/products/${id}/active`, { method: 'PATCH', body: { isActive } }),

  // Suppliers (small list — no pagination needed)
  getSuppliers: () => req('/suppliers'),
  getSuppliersLookup: () => req('/suppliers/lookup'),
  createSupplier: (data) => req('/suppliers', { method: 'POST', body: data }),
  updateSupplier: (id, data) => req(`/suppliers/${id}`, { method: 'PUT', body: data }),
  deleteSupplier: (id) => req(`/suppliers/${id}`, { method: 'DELETE' }),

  // Sales — backend paginated
  getSales: (params = {}) => req('/sales' + toQS(params)),
  createSale: (data) => req('/sales', { method: 'POST', body: data }),
  // No deleteSale — sales are permanent ledger entries once recorded.

  // Table Orders — cafe-style, table-wise sale entry
  getTableOrders:   ()               => req('/table-orders'),
  getTableOrder:    (n)              => req(`/table-orders/${n}`),
  addTableItem:     (n, data)        => req(`/table-orders/${n}/items`, { method: 'POST', body: data }),
  updateTableItem:  (n, itemId, data)=> req(`/table-orders/${n}/items/${itemId}`, { method: 'PUT', body: data }),
  removeTableItem:  (n, itemId)      => req(`/table-orders/${n}/items/${itemId}`, { method: 'DELETE' }),
  payTable:         (n)              => req(`/table-orders/${n}/pay`, { method: 'POST' }),
  cancelTable:      (n)              => req(`/table-orders/${n}`, { method: 'DELETE' }),

  // Purchases — backend paginated
  getPurchases: (params = {}) => req('/purchases' + toQS(params)),
  createPurchase: (data) => req('/purchases', { method: 'POST', body: data }),
  // No deletePurchase — purchases are permanent ledger entries once recorded.

  // Bulk Import
  bulkImport: (data) => req('/bulk-import', { method: 'POST', body: data }),

  // Reports
  getDashboard: () => req('/reports/dashboard'),
  getDailyReport: (date) => req(`/reports/daily?date=${date}`),
  getMonthlyReport: (year, month) => req(`/reports/monthly?year=${year}&month=${month}`),
  getYearlyReport: (year) => req(`/reports/yearly?year=${year}`),
};

export const fmt = {
  currency: (n) => `Rs. ${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  date: (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
  time: (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  datetime: (d) => `${fmt.date(d)} ${fmt.time(d)}`,
};

// ── Calendar ──────────────────────────────────────────────────────────────────
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

Object.assign(api, {
  getCalendarDay:    (date)        => req(`/calendar/day?date=${date}`),
  getCalendarMonth:  (year, month) => req(`/calendar/month?year=${year}&month=${month}`),
  // Uses getCalendarDay with today's local date — avoids server timezone issues
  getTodayReminders: ()            => req(`/calendar/day?date=${todayISO()}`).then(r => r.reminders ?? []),
  createReminder:    (date, text)  => req('/calendar/reminders', { method: 'POST', body: { date, text } }),
  deleteReminder:    (id)          => req(`/calendar/reminders/${id}`, { method: 'DELETE' }),
});

// ── Chat ──────────────────────────────────────────────────────────────────────
// Note: getMessages and sendMessage go through the normal req() helper.
// The SSE stream is handled directly in Chat.jsx with EventSource + Authorization header
// via a custom fetch-based SSE client (EventSource doesn't support custom headers).
Object.assign(api, {
  getChatMessages: (limit = 100) => req(`/chat/messages?limit=${limit}`),
  sendChatMessage: (text) => req('/chat/messages', { method: 'POST', body: { text } }),
  deleteChatMessage: (id) => req(`/chat/messages/${id}`, { method: 'DELETE' }),
});

// ── Product Shortcuts ───────────────────────────────────────────────────────
Object.assign(api, {
  getShortcuts: () => req('/product-shortcuts'),
  createShortcut: (data) => req('/product-shortcuts', { method: 'POST', body: data }),
  updateShortcut: (id, data) => req(`/product-shortcuts/${id}`, { method: 'PUT', body: data }),
  setShortcutActive: (id, isActive) => req(`/product-shortcuts/${id}/active`, { method: 'PATCH', body: { isActive } }),
});

// ── Quick Notes ──────────────────────────────────────────────────────────────
Object.assign(api, {
  createQuickNote: (rawText) => req('/quick-notes', { method: 'POST', body: { rawText } }),
  getPendingQuickNotes: () => req('/quick-notes/pending'),
  updateQuickNoteItem: (itemId, data) => req(`/quick-notes/items/${itemId}`, { method: 'PUT', body: data }),
  deleteQuickNoteItem: (itemId) => req(`/quick-notes/items/${itemId}`, { method: 'DELETE' }),
  approveQuickNote: (id) => req(`/quick-notes/${id}/approve`, { method: 'POST' }),
  rejectQuickNote: (id) => req(`/quick-notes/${id}/reject`, { method: 'POST' }),
});

// ── Shifts ───────────────────────────────────────────────────────────────────
Object.assign(api, {
  getShifts: (from, to) => req(`/shifts?from=${from}&to=${to}`),
  createShift: (data) => req('/shifts', { method: 'POST', body: data }),
  updateShift: (id, data) => req(`/shifts/${id}`, { method: 'PUT', body: data }),
  deleteShift: (id) => req(`/shifts/${id}`, { method: 'DELETE' }),
});

// ── Export ────────────────────────────────────────────────────────────────────
Object.assign(api, {
  exportSettings: () => req('/export/settings'),
  updateExportSettings: (data) => req('/export/settings', { method: 'PUT', body: data }),
  runExportNow: async () => {
    const token = tokenStore.get();
    const res = await fetch(BASE + '/export/run', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) { tokenStore.clear(); window.location.reload(); throw new Error('Session expired'); }
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const emailError = res.headers.get('X-Email-Error');
    return { blob, emailError };
  },
});
